import React, { useEffect, Suspense, lazy } from 'react';
import { Routes, Route, Navigate, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { debugEnvironment, checkApiConfig } from './utils/environmentDebug';
import heartbeatManager from './utils/heartbeat';
import sessionManager from './utils/sessionManager';
import websocketManager from './utils/websocketManager'; // 使用基础的SockJS实现
import Login from './Login';
import FormList from './FormList';
import FormDesigner from './FormDesigner';
import ToastContainer from './components/ToastContainer';
import FormFillerPage from './FormFillerPage';
import FormFillerPageDisplay from './FormFillerPageDisplay';
import WechatAuth from './components/WechatAuth';
import FormSubmissionsPage from './FormSubmissionsPage';
import FormOverview from './FormOverview';
import AccountSettings from './components/AccountSettings';
import WechatCallback from './components/WechatCallback';
import WechatBindCallback from './components/WechatBindCallback';
import { isWechatBrowser } from './config/wechat';
import FormWheel from './components/FormWheel';
import LotteryPage from './components/LotteryPage';
import LotteryFormExample from './components/LotteryFormExample';
import LotteryActivityManager from './components/LotteryActivityManager';

// 懒加载大型组件
const AIAssistant = lazy(() => import('./AIAssistant'));
const MaterialLibrary = lazy(() => import('./MaterialLibrary'));
const KnowledgeBase = lazy(() => import('./components/KnowledgeBase'));
const BlackCardManagement = lazy(() => import('./components/BlackCardManagement'));
const BlackCardActivation = lazy(() => import('./components/BlackCardActivation'));
const BlackCardPrivilegeActivation = lazy(() => import('./components/BlackCardPrivilegeActivation'));
const BlackCardActivationMobile = lazy(() => import('./components/BlackCardActivationMobile'));
const BlackCardActivationManagement = lazy(() => import('./components/BlackCardActivationManagement'));
const AllCardNumbersList = lazy(() => import('./components/AllCardNumbersList'));
const AIServiceTest = lazy(() => import('./components/AIServiceTest'));
const ActivityPlanning = lazy(() => import('./components/ActivityPlanning'));
const DialogueAI = lazy(() => import('./components/DialogueAI'));
const AIServicesHub = lazy(() => import('./components/AIServicesHub'));
const PostEventFeedback = lazy(() => import('./components/PostEventFeedback'));
const FeedbackManagement = lazy(() => import('./components/FeedbackManagement'));
const HouseInspectionManagement = lazy(() => import('./components/HouseInspectionManagement'));
const BenefitCardWriteOffManagement = lazy(() => import('./components/BenefitCardWriteOffManagement'));
const MerchantStoreInfoManagement = lazy(() => import('./components/MerchantStoreInfoManagement'));
const PriceComparisonAI = lazy(() => import('./components/PriceComparisonAI'));
const ProductPricing = lazy(() => import('./components/ProductPricing'));
const HeartbeatStatus = lazy(() => import('./components/HeartbeatStatus'));
const SimpleWebhookChat = lazy(() => import('./components/SimpleWebhookChat'));
const WebhookUploadTest = lazy(() => import('./components/WebhookUploadTest'));
const SignInSheetList = lazy(() => import('./components/SignInSheetList'));
const SignInSheetForm = lazy(() => import('./components/SignInSheetForm'));
const AppointmentSignInForm = lazy(() => import('./components/AppointmentSignInForm'));
const HouseInspectionForm = lazy(() => import('./components/HouseInspectionForm'));
const AppointmentForm = lazy(() => import('./components/AppointmentForm'));
const MerchantSignInList = lazy(() => import('./components/MerchantSignInList'));
const MemberBenefitList = lazy(() => import('./components/MemberBenefitList'));
// 直接使用环境变量
const isDevelopment = () => process.env.NODE_ENV === 'development';
const isStaging = () => process.env.NODE_ENV === 'staging';


function App() {
  const location = useLocation();
  const navigate = useNavigate();
  const token = localStorage.getItem('token');

  // 允许未登录访问的白名单页面
  const allowAnonymous = [
    /^\/login/,
    /^\/detail\//, // 表单填写页
    /^\/wechat-auth/,
    /^\/wechat-callback/,
    /^\/wechat-bind-callback/,
    /^\/sign-in-sheet-form/, // 签到表单页面（不需要微信授权）
    /^\/house-inspection-form/, // 验房表单页面（不需要微信授权）
    /^\/appointment/,
    /^\/appointment-sign-in/ // 预约页面（不需要微信授权）
  ];

  const isAllowed = allowAnonymous.some(reg => reg.test(location.pathname));

  React.useEffect(() => {
    // 环境调试 - 在测试环境和准生产环境中都显示
    if (isDevelopment() || isStaging()) {
      debugEnvironment();
      checkApiConfig();
    }
      
    // 心跳机制和 WebSocket 管理
    if (location.pathname.startsWith('/merchant-sign-in')) {
      // 仅在商家签到列表页面启动 WebSocket
      console.log('商家签到列表页面，启动 WebSocket');
      websocketManager.start();
        
      // 商家签到列表页面不需要心跳和会话管理（通常是匿名访问）
      heartbeatManager.stop();
      sessionManager.destroy();
    } else if (token && !isAllowed) {
      // 其他登录页面启动心跳、会话管理，但不启动 WebSocket
      console.log('用户已登录，启动心跳机制、会话管理，停止 WebSocket');
      heartbeatManager.start();
      sessionManager.init();
      websocketManager.stop();
    } else {
      // 其他页面停止所有服务
      console.log('非商家签到列表页面，停止所有服务');
      websocketManager.stop();
      heartbeatManager.stop();
      sessionManager.destroy();
    }
    
    // 页面可见性变化时处理心跳和WebSocket
    const handleVisibilityChange = () => {
      if (document.hidden) {
        console.log('页面不可见，暂停心跳');
        // 页面不可见时可以考虑保持WebSocket连接
      } else {
        console.log('页面可见，恢复心跳并检查WebSocket连接');
        if (location.pathname.startsWith('/merchant-sign-in')) {
          // 商家签到列表页面检查WebSocket连接
          if (!websocketManager.getStatus().isConnected) {
            console.log('商家签到列表页面，WebSocket未连接，重新连接');
            websocketManager.connect();
          }
        } else if (token && !isAllowed) {
          // 其他登录页面只处理心跳，不处理WebSocket
          heartbeatManager.triggerHeartbeat();
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    // 清理函数
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };

  }, [token, isAllowed, location, navigate]);

  // 加载组件
  const LoadingSpinner = () => (
    <div style={{ 
      display: 'flex', 
      justifyContent: 'center', 
      alignItems: 'center', 
      height: '200px',
      fontSize: '16px',
      color: '#666'
    }}>
      <div style={{ marginRight: '10px' }}>⏳</div>
      加载中...
    </div>
  );

  return (
    <>
      <ToastContainer />
      <HeartbeatStatus />
      <Suspense fallback={<LoadingSpinner />}>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/forms" element={<FormList />} />
          <Route path="/overview" element={<FormOverview />} />
          <Route path="/forms/edit/:formId" element={<FormDesigner />} />
          <Route path="/detail/:formId" element={<FormFillerPage />} />
          <Route path="/display/:formId" element={<FormFillerPageDisplay />} />
          <Route path="/wechat-auth" element={<WechatAuth />} />
          <Route path="/wechat-callback" element={<WechatCallback />} />
          <Route path="/wechat-bind-callback" element={<WechatBindCallback />} />
          <Route path="/forms/:formId/submissions" element={<FormSubmissionsPage />} />
          <Route path="/account-settings" element={<AccountSettings />} />
          <Route path="/ai-assistant" element={<AIAssistant />} />
          <Route path="/material-library" element={<MaterialLibrary />} />
          <Route path="/knowledge-base" element={<KnowledgeBase />} />
          <Route path="/black-card-management" element={<BlackCardManagement />} />
          <Route path="/black-card-activation" element={<BlackCardActivation />} />
          <Route path="/black-card-privilege-activation" element={<BlackCardPrivilegeActivation />} />
          <Route path="/black-card-activation-management" element={<BlackCardActivationManagement />} />
          <Route path="/all-card-numbers" element={<AllCardNumbersList />} />
          <Route path="/black-card-activation-mobile" element={<BlackCardActivationMobile />} />
          <Route path="/product-pricing" element={<ProductPricing />} />
          <Route path="/ai-services" element={<AIServicesHub />} />
          <Route path="/ai-service-test" element={<AIServiceTest />} />
          <Route path="/activity-planning" element={<ActivityPlanning />} />
          <Route path="/dialogue-ai" element={<DialogueAI />} />
          <Route path="/price-comparison-ai" element={<PriceComparisonAI />} />
          <Route path="/post-event-feedback" element={<PostEventFeedback />} />
          <Route path="/feedback-management" element={<FeedbackManagement />} />
          <Route path="/house-inspection-management" element={<HouseInspectionManagement />} />
          <Route path="/benefit-card-write-off-management" element={<BenefitCardWriteOffManagement />} />
          <Route path="/merchant-store-info-management" element={<MerchantStoreInfoManagement />} />
          <Route path="/member-benefit-list" element={<MemberBenefitList />} />
          <Route path="/webhook-chat" element={<SimpleWebhookChat />} />
          <Route path="/webhook-upload" element={<WebhookUploadTest />} />
          <Route path="/sign-in-sheet" element={<SignInSheetList />} />
          <Route path="/merchant-sign-in" element={<MerchantSignInList />} />
          <Route path="/sign-in-sheet-form" element={<SignInSheetForm />} />
          <Route path="/appointment-sign-in" element={<AppointmentSignInForm />} />
          <Route path="/house-inspection-form" element={<HouseInspectionForm />} />
          <Route path="/appointment" element={<AppointmentForm />} />
          <Route path="/form-wheel" element={<FormWheel />} />
          <Route path="/lottery/:formId" element={<LotteryPage />} />
          <Route path="/lottery-form" element={<LotteryFormExample />} />
          <Route path="/lottery-activity" element={<LotteryActivityManager />} />
          {/* 默认重定向 - 智能重定向到合适的页面 */}
          <Route path="/" element={<DynamicHome />} />
          {/* 通配符路由 - 处理未匹配的路径，重定向到表单列表 */}
          <Route path="*" element={<Navigate to="/forms" replace />} />
        </Routes>
      </Suspense>
    </>
  );
}


// 动态首页组件 - 智能重定向
function DynamicHome() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  useEffect(() => {
    console.log('🏠 DynamicHome: 开始智能重定向判断');
    
    // 检查是否有微信授权回调参数
    const code = searchParams.get('code');
    const state = searchParams.get('state');
    const authSuccess = searchParams.get('auth_success');
    
    if (code && state) {
      console.log('🔄 DynamicHome: 检测到微信授权回调，跳转到授权处理页面');
      navigate(`/wechat-callback?code=${code}&state=${state}`, { replace: true });
      return;
    }
    
    if (authSuccess === '1') {
      console.log('✅ DynamicHome: 检测到授权成功标记');
      // 授权成功后的处理逻辑
      const returnUrl = localStorage.getItem('returnUrl');
      if (returnUrl) {
        console.log('🔄 DynamicHome: 使用保存的返回URL:', returnUrl);
        localStorage.removeItem('returnUrl');
        navigate(returnUrl, { replace: true });
        return;
      }
    }
    
    // 检查是否从特定页面跳转过来
    const referrer = document.referrer;
    console.log('🔍 DynamicHome: 检查来源页面:', referrer);
    
    if (referrer) {
      try {
        const referrerUrl = new URL(referrer);
        const referrerPath = referrerUrl.pathname;
        
        // 如果从表单详情页跳转过来
        if (referrerPath.includes('/detail/')) {
          const formId = referrerPath.split('/detail/')[1];
          console.log('📋 DynamicHome: 从表单详情页跳转，显示表单展示页:', formId);
          navigate(`/display/${formId}`, { replace: true });
          return;
        }
        
        // 如果从签到页面跳转过来
        if (referrerPath.includes('/sign-in-sheet-form')) {
          console.log('📝 DynamicHome: 从签到页面跳转，显示签到页面');
          navigate('/sign-in-sheet-form', { replace: true });
          return;
        }

        // 如果从黑卡页面跳转过来
        if (referrerPath.includes('/black-card')) {
          console.log('🃏 DynamicHome: 从黑卡页面跳转，显示黑卡展示页');
          navigate('/black-card-activation-mobile', { replace: true });
          return;
        }
      } catch (error) {
        console.warn('DynamicHome: 解析referrer失败:', error);
      }
    }
    
    // 检查微信环境和登录状态
    const isWechat = isWechatBrowser();
    const isLoggedIn = localStorage.getItem('isWechatLoggedIn') === '1' || 
                      localStorage.getItem('isLoggedIn') === '1';
    
    console.log('🔍 DynamicHome: 环境检查', { isWechat, isLoggedIn });
    
    if (isWechat && !isLoggedIn) {
      console.log('📱 DynamicHome: 微信环境未登录，跳转到微信授权');
      navigate('/wechat-auth', { replace: true });
      return;
    }
    
    // 默认跳转到表单列表
    console.log('🏠 DynamicHome: 默认跳转到表单列表');
    navigate('/forms', { replace: true });
  }, [navigate, searchParams]);
  
  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '100vh',
      background: '#f5f7fa'
    }}>
      <div style={{
        width: 40,
        height: 40,
        border: '4px solid #f3f3f3',
        borderTop: '4px solid #1890ff',
        borderRadius: '50%',
        animation: 'spin 1s linear infinite'
      }}></div>
      <p style={{ marginTop: 16, color: '#666' }}>正在跳转...</p>
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}

export default App;